/**
 * Typowane błędy katalogu wzorów.
 *
 * Reguła z planu §5.3 (odwrócenie PackFlow): brakująca zmienna nieopcjonalna
 * ma RZUCIĆ, a nie wydrukować pustą etykietę. Pusta etykieta wysyłkowa, która
 * fizycznie wyszła z drukarki, jest gorsza niż nieudane zadanie — w PackFlow
 * brakujące `shipping_lines` drukowało puste pole „Region" miesiącami i nikt
 * tego nie widział. Każda klasa ma stały `code`, żeby print-core mógł mapować
 * błędy renderu na kody HTTP bez zgadywania po treści komunikatu.
 */

export class PrintTemplateError extends Error {
  constructor(
    /** Maszynowy kod błędu — stabilny kontrakt dla warstwy HTTP. */
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'PrintTemplateError';
  }
}

/** Slug spoza katalogu — cicha zamiana na „jakiś" wzór byłaby gorsza niż błąd. */
export class UnknownPrintTemplateError extends PrintTemplateError {
  constructor(public readonly slug: string) {
    super('unknown_template', `Nie znaleziono wzoru wydruku o slugu „${slug}"`);
    this.name = 'UnknownPrintTemplateError';
  }
}

/** Brak zmiennej nieopcjonalnej — NIGDY pusta etykieta. */
export class MissingPrintVariableError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    /** Wszystkie brakujące naraz — wołający naprawia jedną rundą, nie pięcioma. */
    public readonly variables: string[]
  ) {
    super('missing_variable', `Wzór „${slug}": brak wymaganych zmiennych: ${variables.join(', ')}`);
    this.name = 'MissingPrintVariableError';
  }
}

/**
 * Układ używa `{teraz}`, a wołający nie podał chwili wydruku.
 *
 * Osobny błąd, a nie ciche pominięcie: interpreter jest CZYSTY i zegara nie
 * czyta (to jego niezmiennik — ten sam kod maluje płótno w przeglądarce),
 * więc chwilę wstrzykuje wołający. Pominięta znaczy, że tor druku ma usterkę
 * w okablowaniu — a pusta data na etykiecie produkcyjnej wygląda jak brak
 * wymagań, nie jak awaria.
 */
export class MissingPrintClockError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    public readonly shapes: string[]
  ) {
    super(
      'missing_clock',
      `Wzór „${slug}" używa chwili wydruku (${shapes.join(', ')}), a render nie dostał zegara`
    );
    this.name = 'MissingPrintClockError';
  }
}

/** Cel niezadeklarowany przez wzór (np. ESCPOS dla etykiety partii). */
export class UnsupportedPrintTargetError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    public readonly target: string
  ) {
    super(
      'unsupported_target',
      `Wzór „${slug}" nie deklaruje celu „${target}" — druk na nim dałby śmieci z drukarki`
    );
    this.name = 'UnsupportedPrintTargetError';
  }
}

/**
 * Odstępstwo tenanta (Liquid) dla celu termicznego. W v1 tenant override
 * istnieje WYŁĄCZNIE dla PDF (Liquid → HTML → pipeline dokumentowy);
 * języków komend ZPL/EPL/TSPL/ESC-POS nie da się bezpiecznie edytować
 * szablonem tekstowym — patrz komentarz przy `RenderPrintInput.tenantLiquid`.
 */
export class TenantLiquidUnsupportedError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    public readonly target: string
  ) {
    super(
      'tenant_liquid_unsupported',
      `Odstępstwo Liquid tenanta obsługuje wyłącznie cel PDF — „${slug}" zażądał „${target}"`
    );
    this.name = 'TenantLiquidUnsupportedError';
  }
}

/**
 * Zepsute odstępstwo tenanta = twardy, kodowany błąd — bez cichego fallbacku
 * na wzór domyślny (reguła katalogu e-mail: „cicha zamiana jest gorsza").
 */
export class TenantLiquidRenderError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    detail: string
  ) {
    super('tenant_liquid_render', `Odstępstwo tenanta dla „${slug}" nie renderuje się: ${detail}`);
    this.name = 'TenantLiquidRenderError';
  }
}

/**
 * Cel PDF bez wstrzykniętego renderera. Pakiet bazowy jest importowalny
 * w przeglądarce, więc NIE zaciąga Puppeteera — serwis renderujący podaje
 * `renderPdf` z subpath `@insolitum/print-templates/pdf` (plan §5.2: render
 * w chmurze, nigdy w agencie).
 */
export class MissingPdfRendererError extends PrintTemplateError {
  constructor(public readonly slug: string) {
    super(
      'missing_pdf_renderer',
      `Cel PDF wymaga renderera — przekaż \`pdf\` z „@insolitum/print-templates/pdf" (wzór „${slug}")`
    );
    this.name = 'MissingPdfRendererError';
  }
}

/** Dane kodu kreskowego poza charsetem symboliki — wykryte w enkoderze, przed drukiem. */
export class BarcodeCharsetError extends PrintTemplateError {
  constructor(
    public readonly symbology: string,
    public readonly data: string,
    detail: string
  ) {
    super('barcode_charset', `Kod ${symbology}: ${detail} (dane: „${data}")`);
    this.name = 'BarcodeCharsetError';
  }
}

/** Brak ścieżki Chromium — subpath ./pdf świadomie NICZEGO nie pobiera. */
export class MissingChromiumError extends PrintTemplateError {
  constructor() {
    super(
      'missing_chromium',
      'Brak przeglądarki do renderu PDF: ustaw PRINT_CHROMIUM_PATH (albo PUPPETEER_EXECUTABLE_PATH). ' +
        'Pakiet celowo nie pobiera Chromium — obraz print-core dostarcza go w Dockerfile (plan, PR-2b).'
    );
    this.name = 'MissingChromiumError';
  }
}

// ---------------------------------------------------------------------------
// Błędy warstwy UKŁADU (edytor tenanta, `src/uklad/`).
//
// Wspólny powód istnienia tej sekcji: układ jest PIERWSZYM źródłem IR w tym
// systemie, które nie pochodzi z TypeScriptu — przyjeżdża JSON-em z bazy,
// napisany przez człowieka w panelu. Wszystko, co dotąd gwarantował
// kompilator (domknięcie unii `PrintNode`, istnienie zmiennej, poprawność
// `media`), tutaj musi sprawdzić kod, z typowanym kodem błędu, żeby print-core
// mapował na HTTP bez zgadywania po treści komunikatu.
// ---------------------------------------------------------------------------

/**
 * Węzeł o rodzaju spoza `PrintNode` — dotąd niemożliwy, bo `switch` bez
 * `default` w enkoderze po prostu zwracał `undefined`. To `undefined` sklejone
 * z payloadem wychodziło do drukarki jako string „undefined": Zebra drukowała
 * napis, XPrinter gubił resztę etykiety. Enkodery mają teraz `default:`, który
 * rzuca tym błędem.
 */
export class UnknownPrintNodeError extends PrintTemplateError {
  constructor(public readonly nodeKind: unknown) {
    super('unknown_node', `Nieznany rodzaj węzła wydruku: „${String(nodeKind)}"`);
    this.name = 'UnknownPrintNodeError';
  }
}

/** Media spoza słownika (`kind`/`page` z JSON-a) — zamiast `TypeError` z destrukturyzacji. */
export class UnknownPrintMediaError extends PrintTemplateError {
  constructor(public readonly media: unknown) {
    super('unknown_media', `Nieznane media wydruku: ${JSON.stringify(media)}`);
    this.name = 'UnknownPrintMediaError';
  }
}

/** Jeden problem znaleziony przez `walidujUklad` — ścieżka wskazuje węzeł w panelu. */
export interface BladUkladu {
  /** Ścieżka w układzie, np. `nodes[3].zrodlo.wiersz[1].tekst[0]`. */
  sciezka: string;
  /** Maszynowy kod — panel dobiera po nim podpowiedź, nie po treści. */
  kod: string;
  komunikat: string;
  /** Identyfikator węzła, jeśli problem da się do niego przypiąć. */
  nodeId?: string;
}

/**
 * Układ nie przechodzi walidacji. Niesie KOMPLET błędów, nie pierwszy —
 * ta sama zasada co `MissingPrintVariableError`: człowiek poprawia jedną
 * rundą, nie pięcioma.
 */
export class InvalidPrintLayoutError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    public readonly bledy: BladUkladu[]
  ) {
    super(
      'invalid_layout',
      `Układ wzoru „${slug}" jest niepoprawny (${bledy.length}): ` +
        bledy.map((b) => `${b.sciezka}: ${b.komunikat}`).join('; ')
    );
    this.name = 'InvalidPrintLayoutError';
  }
}

/**
 * Katalog odjechał od zapisanego układu: wzór dołożył zmienną nieopcjonalną
 * (albo zmienił media), a układ o niej nie wie. Cichy render dałby etykietę
 * bez nowego, wymaganego pola — dlatego to błąd, a w panelu chip „wymaga
 * przeglądu" z ofertą ponownego zasiewu.
 */
export class LayoutContractDriftError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    public readonly wersjaUkladu: number,
    public readonly wersjaKatalogu: number,
    public readonly szczegoly: string
  ) {
    super(
      'layout_contract_drift',
      `Układ wzoru „${slug}" powstał na wersji ${wersjaUkladu}, katalog jest na ${wersjaKatalogu}: ${szczegoly}`
    );
    this.name = 'LayoutContractDriftError';
  }
}

/**
 * Wołający podał JEDNOCZEŚNIE układ i odstępstwo Liquid.
 *
 * Nie „układ wygrywa" i nie „Liquid wygrywa": wiersz odstępstwa niesie
 * DOKŁADNIE jedną postać (CHECK `print_templates_tresc_check`), więc oba naraz
 * znaczą pomyłkę w warstwie serwisu, a cichy wybór jednej z nich drukowałby
 * coś innego, niż tenant opublikował.
 */
export class TenantOverrideConflictError extends PrintTemplateError {
  constructor(public readonly slug: string) {
    super(
      'tenant_override_conflict',
      `Wzór „${slug}": podano jednocześnie układ i odstępstwo Liquid`
    );
    this.name = 'TenantOverrideConflictError';
  }
}

/** Wiązanie do zmiennej, której wzór nie deklaruje — literówka w edytorze. */
export class UnknownLayoutVariableError extends PrintTemplateError {
  constructor(
    public readonly slug: string,
    public readonly variables: string[]
  ) {
    super(
      'unknown_layout_variable',
      `Układ wzoru „${slug}" odwołuje się do nieistniejących zmiennych: ${variables.join(', ')}`
    );
    this.name = 'UnknownLayoutVariableError';
  }
}

/**
 * Powtórzenie o `zrodlo.rodzaj` spoza gramatyki. Osobna klasa od
 * `UnknownPrintNodeError`, bo to inne miejsce w JSON-ie i inna podpowiedź
 * w panelu: rodzaj węzła wybiera się z palety, źródło wierszy — z listy
 * rozwijanej wewnątrz bloku powtórzenia.
 *
 * Bez tej gałęzi `switch` zostawiał `rows` bez przypisania, a enkodery ginęły
 * na `TypeError: Cannot read properties of undefined (reading 'flatMap')` —
 * błędzie bez kodu, którego print-core nie umiał zmapować na HTTP.
 */
export class UnknownLayoutSourceError extends PrintTemplateError {
  constructor(public readonly rodzaj: unknown) {
    super('unknown_layout_source', `Nieznane źródło wierszy powtórzenia: „${String(rodzaj)}"`);
    this.name = 'UnknownLayoutSourceError';
  }
}
